import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, BookOpen, Feather } from "lucide-react";
import Emblem from "./Emblem";
import { IMAGES } from "@/data";
import { useLang } from "@/i18n/LangContext";
import { scrollToSection } from "@/lib/scroll";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t, content } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <motion.div style={{ y: bgY, opacity: bgOpacity }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.portrait}
          alt="Retrato en penumbra — Javier Alagrande"
          className="w-full h-full object-cover object-center opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0E]/70 via-[#0D0D0E]/55 to-[#0D0D0E]" />
        <div className="absolute inset-0 spotlight-glow" />
      </motion.div>

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-4xl mx-auto px-6 text-center pt-28 pb-24"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex justify-center mb-8"
        >
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(closest-side,rgba(110,150,190,0.20),transparent_75%)] scale-150" aria-hidden="true" />
          <Emblem className="w-52 sm:w-72 h-auto drop-shadow-[0_18px_40px_rgba(0,0,0,0.6)]" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="font-code text-[11px] sm:text-xs tracking-[0.4em] uppercase text-[#C5A059] mb-6"
          data-testid="hero-overline"
        >
          {t("hero.overline")}
        </motion.p>

        <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-[#F4F1EA]">
          <MaskedLine delay={0.35} className="pb-1">JAVIER</MaskedLine>
          <MaskedLine delay={0.55} className="pb-2">
            <span className="italic text-[#D4AF37]">ALAGRANDE</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.85} className="mt-8">
          <p
            data-testid="hero-phrase"
            className="font-display italic text-lg sm:text-2xl text-[#D8D4C8] max-w-2xl mx-auto leading-snug"
          >
            «{content.author.phrase}»
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 space-y-1.5 max-w-xl mx-auto"
          data-testid="hero-bio"
        >
          {content.author.bio.map((line, i) => (
            <p key={i} className="text-sm sm:text-base text-[#A0A0A5] leading-relaxed">
              {line}
            </p>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            data-testid="hero-cta-obras"
            onClick={() => scrollToSection("#libros")}
            className="group flex items-center gap-3 px-8 py-4 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#E5C158] transition-colors duration-300"
          >
            <BookOpen className="w-4 h-4 transition-transform duration-300 group-hover:-rotate-12" />
            {t("hero.cta1")}
          </button>
          <button
            data-testid="hero-cta-bitacora"
            onClick={() => scrollToSection("#bitacora")}
            className="group flex items-center gap-3 px-8 py-4 gold-hairline text-[#D4AF37] text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors duration-300"
          >
            <Feather className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
            {t("hero.cta2")}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToSection("#libros")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#C5A059] scroll-cue"
        aria-label="Desplazarse"
      >
        <ChevronDown className="w-6 h-6" />
      </motion.button>
    </section>
  );
};

export default Hero;
